'use client'

import { useState, useEffect, useContext } from 'react'
import { Loader2 } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'
import Usage from '@/app/manager/settings/components/Usage'
import BillingTier from '@/app/manager/settings/components/BillingTier'
import BillingNextPayment from '@/app/manager/settings/components/BillingNextPayment'
import BillingHistory from '@/app/manager/settings/components/BillingHistory'
import PaymentMethods from '@/app/manager/settings/components/PaymentMethods'
import { calculateTotalCost } from '@/config/constants/billing'
import BillingLimits from '../components/BillingLimits'

export default function Billing() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [billingDetails, setBillingDetails] = useState(null)
  const [tierDetails, setTierDetails] = useState(null)
  const [currentTier, setCurrentTier] = useState(null)
  const [usage, setUsage] = useState(null)
  const [usageError, setUsageError] = useState(null)
  const [usageLoading, setUsageLoading] = useState(true)
  const [appliedDiscount, setAppliedDiscount] = useState(null)
  const authContext = useContext(AuthContext)

  useEffect(() => {
    fetchBillingDetails()
  }, [authContext])

  const fetchBillingDetails = async () => {
    try {
      const { data: tiers } = await axios(
        `/organization/${authContext.user.organization.id}/tiers`,
        { method: 'GET' },
        authContext,
        true, // Set Content-Type
        'billing', // Use Billing URL
      )
      setTierDetails(tiers)

      const { data: billing } = await axios(
        `/organization/${authContext.user.organization.id}`,
        { method: 'GET' },
        authContext,
        true, // Set Content-Type
        'billing', // Use Billing URL
      )
      setBillingDetails(billing)
      setCurrentTier(billing.tier)

      // Fetch applied discount information if there's an active subscription
      if (billing.subscriptionId) {
        try {
          const { data: discount } = await axios(
            `/organization/${authContext.user.organization.id}/applied-discount`,
            { method: 'GET' },
            authContext,
            true, // Set Content-Type
            'billing', // Use Billing URL
          )
          setAppliedDiscount(discount)
        } catch {
          // No discount applied or endpoint doesn't exist yet
          setAppliedDiscount(null)
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load billing details')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await axios(
        `/organization/${authContext.user.organization.id}/limits`,
        { method: 'POST', data: billingDetails },
        authContext,
        true, // Set Content-Type
        'billing', // Use Billing URL
      )
      setError(null)
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update billing limits')
    } finally {
      setSaving(false)
    }
  }

  const handleChange = (field, value) => {
    setBillingDetails(prev => ({
      ...prev,
      [field]: parseInt(value) || 0,
    }))
  }

  const getCurrentTierPrice = () => {
    const currentTierData = tierDetails?.find(tier => tier.name === currentTier)
    return currentTierData?.price || 0
  }

  const calculateTotalMonthlyCost = () => {
    const tierPrice = getCurrentTierPrice()
    const additionalCost = calculateTotalCost(billingDetails)
    return tierPrice + additionalCost
  }

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        const { data } = await axios(
          `/organization/${authContext.user.organization.id}/current-usage`,
          { method: 'GET' },
          authContext,
          true, // Set Content-Type
          'billing', // Use Billing URL
        )
        setUsage(data)
      } catch {
        setUsageError('Failed to load usage data')
      } finally {
        setUsageLoading(false)
      }
    }

    fetchUsage()
  }, [authContext])

  if (loading) {
    return (
      <div className="vertically-centered">
        <Loader2 className="size-6 animate-spin" />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-4 flex-1">
      <div className="grid gap-4">
        <BillingTier
          tierDetails={tierDetails}
          currentTier={currentTier}
          setCurrentTier={setCurrentTier}
          billingDetails={billingDetails}
          onTierUpdate={fetchBillingDetails}
        />
        <PaymentMethods
          billingDetails={billingDetails}
          currentTier={currentTier}
        />
        <BillingLimits
          calculateTotalMonthlyCost={calculateTotalMonthlyCost}
          getCurrentTierPrice={getCurrentTierPrice}
          billingDetails={billingDetails}
          handleChange={handleChange}
          currentTier={currentTier}
          handleSave={handleSave}
          saving={saving}
          error={error}
        />
      </div>

      <div className="grid gap-4">
        <BillingNextPayment
          billingDetails={billingDetails}
          tierDetails={tierDetails}
          currentTier={currentTier}
          usage={usage}
          appliedDiscount={appliedDiscount}
        />
        <BillingHistory currentTier={currentTier} />
        <Usage
          tierDetails={tierDetails}
          billingDetails={billingDetails}
          usage={usage}
          loading={usageLoading}
          error={usageError}
        />
      </div>
    </div>
  )
}
